import { Action } from 'redux'

import ClientPassengerSSRInterface from '../../interfaces/client/PassengerSSR'
import PassengerSSRInterface from '../../interfaces/client/PassengerSSR'
import PassengerSSRsStateInterface from '../../interfaces/client/PassengerSSRsState'
import PassengerSSRDimensionsInterface from '../../interfaces/client/PassengerSSRDimensions'
import PassengerSSROptionsInterface from '../../interfaces/client/PassengerSSROptions'

import { ADD_PASSENGER_SSR } from '../../actions/passengers/addPassengerSSR'
import { SET_PASSENGER_SSR_TO_DELETE } from '../../actions/passengers/setPassengerSSRToDelete'
import { UNSET_PASSENGER_SSR_TO_DELETE } from '../../actions/passengers/unsetPassengerSSRToDelete'
import { REMOVE_PASSENGER_SSR } from '../../actions/passengers/removePassengerSSR'
import { RESET_BOOKING } from '../../actions/bookings/resetBooking'

interface PassengerSSRsAction extends Action {
  passengerId: string
  passengerSSRId: string
  ssrId: string
  options: PassengerSSROptionsInterface
  dimensions?: PassengerSSRDimensionsInterface
}

export const initialState: PassengerSSRsStateInterface = {
  byId: {},
  allIds: [],
}

export default (
  state = initialState,
  action: PassengerSSRsAction = {
    type: '',
    passengerId: '',
    passengerSSRId: '',
    ssrId: '',
    options: {
      isConfirmed: false,
      isAbleToDelete: true,
      note: '',
      ssrNumber: 0,
    },
  }
) => {
  const {
    type,
    passengerId,
    passengerSSRId,
    ssrId,
    dimensions,
    options,
  } = action
  const {
    isConfirmed = false,
    isAbleToDelete = true,
    note = '',
    ssrNumber = 0,
  } = options || {}

  switch (type) {
    case ADD_PASSENGER_SSR:
      if (Object.keys(state.byId).includes(passengerId)) {
        const modifiedPassengers = {
          ...state.byId,
          [passengerId]: [
            ...state.byId[passengerId].filter(
              (passengerSSR: PassengerSSRInterface) =>
                passengerSSR.passengerSSRId !== passengerSSRId
            ),
            {
              passengerSSRId,
              ssrId,
              isConfirmed,
              isAbleToDelete,
              dimensions,
              toDelete: false,
              note,
              ssrNumber,
            },
          ],
        }

        return {
          byId: { ...state.byId, ...modifiedPassengers },
          allIds: [...state.allIds],
        }
      }

      const newPassenger = {
        [passengerId]: [
          {
            passengerSSRId,
            ssrId,
            isConfirmed,
            isAbleToDelete,
            dimensions,
            toDelete: false,
            note,
            ssrNumber,
          },
        ],
      }

      return {
        byId: { ...state.byId, ...newPassenger },
        allIds: [...state.allIds, passengerId],
      }

    case REMOVE_PASSENGER_SSR:
      // Given the passengerId is not known
      if (!state.allIds.includes(passengerId)) {
        return state
      }

      const modifiedPassengersById = { ...state.byId }
      const modifiedPassenger = state.byId[passengerId].filter(
        (passengerSSR: ClientPassengerSSRInterface) =>
          passengerSSR.passengerSSRId !== passengerSSRId
      )

      // Given the last SSR is removed from the passenger --> Remove passenger from store
      if (!modifiedPassenger.length) {
        delete modifiedPassengersById[passengerId]

        return {
          byId: modifiedPassengersById,
          allIds: state.allIds.filter(
            (singlePassengerId: string) => singlePassengerId !== passengerId
          ),
        }
      }

      // Given remove one of the SSR from the passenger
      modifiedPassengersById[passengerId] = modifiedPassenger

      return {
        byId: { ...modifiedPassengersById },
        allIds: [...state.allIds],
      }

    case SET_PASSENGER_SSR_TO_DELETE:
      if (!state.byId[action.passengerId]) {
        return state
      }

      const updatedPassengersSSRs = state.byId[action.passengerId].map(
        (passengerSSR: ClientPassengerSSRInterface) =>
          passengerSSR.passengerSSRId === action.passengerSSRId
            ? {
                ...passengerSSR,
                toDelete: true,
              }
            : passengerSSR
      )

      return {
        byId: {
          ...state.byId,
          [action.passengerId]: updatedPassengersSSRs,
        },
        allIds: [...state.allIds],
      }

    case UNSET_PASSENGER_SSR_TO_DELETE:
      if (!state.byId[action.passengerId]) {
        return state
      }

      const changedPassengerSSRs = state.byId[action.passengerId].map(
        (passengerSSR: ClientPassengerSSRInterface) =>
          passengerSSR.passengerSSRId === action.passengerSSRId
            ? {
                ...passengerSSR,
                toDelete: false,
              }
            : passengerSSR
      )

      return {
        byId: {
          ...state.byId,
          [action.passengerId]: changedPassengerSSRs,
        },
        allIds: [...state.allIds],
      }

    case RESET_BOOKING:
    case '@@router/LOCATION_CHANGE':
      return initialState

    default:
      return state
  }
}
